import { classifyOption } from './classifyModifier'
import { resolveGroupOptionPhoto, type OptionPhotoChoice } from './resolveOptionPhoto'
import {
  canRepeat,
  effectiveMin,
  groupMaxUnits,
  maxQtyForOption,
  normalizeGroupRule,
  type ModOptionRule,
} from './modifierQuantity'
import { effectiveUnitPrice, type PricingConfig } from '@/lib/utils/pricing'
import type { NameTranslations } from './localizedName'

export type GuidedModifierOption = {
  id: string
  name: string
  price_delta: number
  is_available: boolean | null
} & OptionPhotoChoice

export type GuidedModifierGroup = {
  id: string
  /** Nombre interno: no se le muestra al cliente (ver localizedGroupLabel). */
  name: string
  /** Rótulo que el dueño escribió para el cliente. */
  display_name?: string | null
  translations?: NameTranslations | null
  required: boolean | null
  min_selections: number | null
  max_selections: number | null
  display_style?: string | null
  /** «Mostrar con fotos» del grupo (false también si el local las apagó). */
  show_photos?: boolean | null
  modifier_options: GuidedModifierOption[]
}

/** Fila del join `item_modifier_groups`. El `sort_order` vive AQUÍ, no en el
 *  grupo: es la posición que el dueño le dio al grupo DENTRO de este ítem. */
export type GuidedJoinRow = {
  sort_order?: number | null
  modifier_groups: GuidedModifierGroup | null
}

export type ExtraOption = {
  groupId: string
  optionId: string
  /** Nombre CANÓNICO. La traducción para mostrar la resuelve la vista. */
  name: string
  /** Delta ya efectivo, para DISPLAY. El motor nunca compone un total. */
  priceDelta: number
  repeatable: boolean
  /** Tope de repetición de ESTA opción puntual (2× queso extra). Para una
   *  opción no repetible siempre es 1, aunque el grupo permita elegir varias
   *  opciones DISTINTAS — no confundir con `groupMaxUnits`. */
  maxQty: number
  /** Tope de UNIDADES del grupo dueño de esta opción (`max_selections`), sin
   *  topar en MAX_MODIFIER_QTY. El paso `extras` mezcla varias opciones de
   *  varios grupos en un solo pool (add/remove), así que sin este campo no
   *  hay forma de saber cuántas opciones DISTINTAS del mismo grupo admite
   *  seleccionar `select` — `maxQty` no sirve para eso (ver arriba). */
  groupMaxUnits: number
  /**
   * 86: el ingrediente se acabó. La opción se MUESTRA igual (apagada, con el
   * rótulo "Agotado") en vez de desaparecer — así el cliente entiende que hoy
   * no hay, no que el local dejó de ofrecerlo. `GuidedItemFlow` la pinta sin
   * onClick. El servidor también lo rechaza antes de cobrar (/session en
   * lib/online-orders/pricing.ts, el kiosko en lib/menu/soldOutGate.ts); la UI
   * apagada evita que el cliente se entere recién al pagar.
   */
  soldOut: boolean
  /**
   * Foto propia del dueño o, si no subió ninguna, la del catálogo compartido
   * de ingredientes (`resolveGroupOptionPhoto`). URL CRUDA: cada vista le aplica
   * su propio menuPhotoUrl(url, ancho). Null cuando la opción está agotada, el
   * dueño la dejó sin foto, el grupo o el local apagaron las fotos, o no hay
   * propia y el catálogo no la cubre.
   */
  photoUrl: string | null
}

export type GuidedQuestionStep = {
  kind: 'question'
  group: GuidedModifierGroup
  min: number
  maxUnits: number
  singleSelect: boolean
  options: ExtraOption[]
}

export type GuidedExtrasStep = {
  kind: 'extras'
  choiceGroups: GuidedQuestionStep[]
  add: ExtraOption[]
  remove: ExtraOption[]
}

export type GuidedStep = GuidedQuestionStep | GuidedExtrasStep

function availableOptions(g: GuidedModifierGroup): GuidedModifierOption[] {
  return g.modifier_options.filter((o) => o.is_available !== false)
}

function toExtraOption(
  g: GuidedModifierGroup,
  o: GuidedModifierOption,
  cfg: PricingConfig
): ExtraOption {
  const rule = normalizeGroupRule(g)
  const optionRules: ModOptionRule[] = availableOptions(g).map((x) => ({
    priceDelta: effectiveUnitPrice(x.price_delta ?? 0, cfg),
  }))
  const priceDelta = effectiveUnitPrice(o.price_delta ?? 0, cfg)
  return {
    groupId: g.id,
    optionId: o.id,
    name: o.name,
    priceDelta,
    repeatable: canRepeat(rule, { priceDelta }),
    maxQty: maxQtyForOption(rule, { priceDelta }, optionRules),
    groupMaxUnits: groupMaxUnits(rule, optionRules),
    soldOut: o.is_available === false,
    // `o.name` es el nombre CANÓNICO. Nunca pasar acá el traducido: el
    // catálogo está en español y un cliente en inglés perdería toda foto.
    // `g.name` es el contexto que desambigua nombres como 'pollo' (ver
    // AMBIGUOUS_NAMES en ingredientCatalog.ts) — también canónico, nunca el
    // display/traducido del grupo. La elección del dueño (foto propia o sin
    // foto) gana sobre el catálogo, y un grupo con las fotos apagadas no lleva
    // ninguna — ver resolveGroupOptionPhoto.
    photoUrl: resolveGroupOptionPhoto(o, g),
  }
}

function toQuestionStep(g: GuidedModifierGroup, cfg: PricingConfig): GuidedQuestionStep {
  const rule = normalizeGroupRule(g)
  const options = availableOptions(g)
  const optionRules: ModOptionRule[] = options.map((o) => ({
    priceDelta: effectiveUnitPrice(o.price_delta ?? 0, cfg),
  }))
  return {
    kind: 'question',
    group: g,
    min: effectiveMin(rule),
    maxUnits: groupMaxUnits(rule, optionRules),
    singleSelect: rule.maxSelections === 1,
    // Se listan TODAS las opciones (las agotadas salen apagadas). Los topes de
    // arriba (min/maxUnits) siguen calculándose sobre `options` = solo las
    // disponibles: la matemática de cantidades no cambia ni un centavo por
    // mostrar una opción que nadie puede elegir.
    options: g.modifier_options.map((o) => toExtraOption(g, o, cfg)),
  }
}

/**
 * Arma la lista de pasos del flujo guiado de un ítem.
 *
 * Invariantes:
 *  - Los pasos `question` son EXACTAMENTE los grupos obligatorios
 *    (`effectiveMin > 0`), en el `sort_order` del join.
 *  - El paso `extras` es SIEMPRE el último y SIEMPRE existe, aunque quede
 *    vacío: es donde viven la nota, la cantidad y el botón de agregar. Si
 *    devolviera [] para un ítem sin modificadores, el plato no se podría
 *    comprar — y el auto-avance de la última pregunta se quedaría sin destino.
 *  - Una opción agotada (86) SÍ genera chip: sale apagada con "Agotado" en vez
 *    de desaparecer, para que el cliente vea que hoy no hay ese ingrediente.
 *  - Pero un grupo cuyas opciones estén TODAS agotadas sigue sin generar paso.
 *    Ese filtro no es cosmético: un obligatorio sin ninguna opción elegible
 *    exige un mínimo imposible y traba el botón de agregar sin salida (el bug
 *    que tenía el microsite). Mostrarlo entero apagado sería el mismo callejón.
 */
export function buildGuidedSteps(
  joinRows: GuidedJoinRow[],
  opts: { pricingCfg: PricingConfig }
): GuidedStep[] {
  const { pricingCfg: cfg } = opts

  const groups = joinRows
    .filter((r): r is GuidedJoinRow & { modifier_groups: GuidedModifierGroup } => !!r.modifier_groups)
    .filter((r) => availableOptions(r.modifier_groups).length > 0)
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((r) => r.modifier_groups)

  const questions: GuidedQuestionStep[] = []
  const choiceGroups: GuidedQuestionStep[] = []
  const add: ExtraOption[] = []
  const remove: ExtraOption[] = []

  for (const g of groups) {
    // Un grupo obligatorio es SIEMPRE una pregunta, tenga el display_style que
    // tenga — incluido el caso real "Obligatorio para perros", que es required
    // y aun así son casi todas remociones.
    if (effectiveMin(normalizeGroupRule(g)) > 0) {
      questions.push(toQuestionStep(g, cfg))
      continue
    }
    for (const o of g.modifier_options) {
      const kind = classifyOption(g, {
        name: o.name,
        priceDelta: effectiveUnitPrice(o.price_delta ?? 0, cfg),
      })
      if (kind === 'choice') {
        if (!choiceGroups.some((c) => c.group.id === g.id)) choiceGroups.push(toQuestionStep(g, cfg))
        continue
      }
      ;(kind === 'remove' ? remove : add).push(toExtraOption(g, o, cfg))
    }
  }

  // Las notas rápidas (`quick_notes`) NO entran acá: son una herramienta INTERNA del
  // POS, no un menú para el cliente. Estaban puestas como chips en este paso y el
  // resultado era que alguien pidiendo un jugo veía "Sin cebolla · Sin hielo · Sin
  // gluten" — etiquetas que no describen el producto y que siembran la duda de si lo
  // trae. No es una anomalía de un local: son el seed que 65 de 72 locales nunca
  // tocaron, idéntico vendan empanadas o jugos. Y `quick_notes` ni siquiera tiene
  // columna de traducciones, así que en un kiosko en inglés salían en español.
  // El cliente sigue teniendo la nota de texto libre, que es donde una petición real
  // ("sin cebolla" sobre algo que SÍ lleva cebolla) tiene sentido.
  const extras: GuidedExtrasStep = {
    kind: 'extras',
    choiceGroups,
    add,
    remove,
  }

  // El paso extras solo sale si TIENE algo que ofrecer. Antes salía siempre, y para un
  // ítem cuyos grupos son todos preguntas quedaba una última pantalla "¿Algo más?" con
  // nada debajo — el "Paso 5 de 5" vacío del jugo. Lo único que la llenaba eran los
  // chips de `quick_notes`, que se fueron por ser una herramienta interna del POS.
  //
  // La excepción es la que ya estaba documentada arriba: sin NINGUNA pregunta, extras
  // es el único paso posible y tiene que salir aunque esté vacío — es donde viven la
  // nota, la cantidad y el botón de agregar. Devolver [] dejaría el plato incomprable.
  const ofreceAlgo = choiceGroups.length > 0 || add.length > 0 || remove.length > 0
  if (questions.length === 0 || ofreceAlgo) return [...questions, extras]
  return questions
}
